// src/services/communityStats.js
//
// ── The one community number ─────────────────────────────────────────────
//
//   "Together, we've completed 15,000+ classes."
//
// Counting this honestly at read time would mean scanning the whole
// `completions` collection on every Home load — thousands of reads per member
// per visit. Instead a single aggregate doc is incremented alongside the
// existing per-user counter, so displaying it costs exactly ONE read.
//
// Product rule (2026-08-24): this total counts EVERYONE, forever — including
// members who have since cancelled. Their work stays part of the total, so the
// counter only ever moves forward. It is deliberately never decremented when
// somebody leaves.
//
// It IS decremented when a completion is deleted (an undo), because that
// completion didn't happen. See adr-milestone-badges.md.

import { doc, getDoc, increment, serverTimestamp } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc } from './firestoreWrite';
import { db, firebaseEnabled } from './firebase';

export const COMMUNITY_DOC = 'global';

// The EXACT total, thousands-separated.
//
// This originally rounded down to the nearest 500 on the theory that a round
// number reads as an achievement rather than an odometer. In practice that
// discarded hundreds of real classes (5,294 showed as "5,000+") and undersold
// the community's actual work. The total only moves a handful of times a day,
// so there is no odometer effect to guard against — and watching it climb after
// a class is the point.
export function formatCommunityTotal(total) {
    const n = Number(total) || 0;
    return { display: n.toLocaleString(), rounded: false };
}

export async function fetchCommunityTotal() {
    if (!firebaseEnabled) return null;
    try {
        const snap = await getDoc(doc(db, 'communityStats', COMMUNITY_DOC));
        if (!snap.exists()) return null;
        const data = snap.data() || {};
        return {
            total: data.totalClassCompletions || 0,
            seededAt: data.seededAt || null,
        };
    } catch (e) {
        // A missing rule or offline device must never break Home — the counter
        // simply doesn't render.
        console.error('Error fetching community total:', e);
        return null;
    }
}

// Fire-and-forget. NEVER awaited by a completion write path: the completion
// record is the source of truth and must not be delayed or failed by a
// counter update.
export function bumpCommunityTotal(delta = 1) {
    if (!firebaseEnabled || !delta) return;
    try {
        setDoc(
            doc(db, 'communityStats', COMMUNITY_DOC),
            { totalClassCompletions: increment(delta), updatedAt: serverTimestamp() },
            { merge: true },
        ).catch((e) => console.error('Error bumping community total:', e));
    } catch (e) {
        console.error('Error bumping community total:', e);
    }
}
